import React from "react";
import Modal from "react-responsive-modal";
import { FiLoader, FiX } from "react-icons/fi";
import {
  MAX_PHOTO_AI_PROMPT_LENGTH,
  sanitizePhotoAiPrompt,
} from "../../utils/photoAiPrompt";

type PhotoAiPromptModalProps = {
  open: boolean;
  prompt: string;
  photoPreviewUrl?: string;
  isLoading: boolean;
  onPromptChange: (value: string) => void;
  onClose: () => void;
  onSubmit: () => void;
  onCancelProcessing: () => void;
};

export default function PhotoAiPromptModal({
  open,
  prompt,
  photoPreviewUrl,
  isLoading,
  onPromptChange,
  onClose,
  onSubmit,
  onCancelProcessing,
}: PhotoAiPromptModalProps) {
  const sanitizedLength = sanitizePhotoAiPrompt(prompt).length;

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!isLoading) onSubmit();
  }

  return (
    <Modal
      open={open}
      onClose={isLoading ? onCancelProcessing : onClose}
      classNames={{ modal: "photo-ai-prompt-modal" }}
      showCloseIcon={false}
      center
    >
      <div className="w-full p-1 sm:p-2">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Editar imagem com IA
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Descreva exatamente o que deseja alterar na foto (ex.: remover
              folhas do chão, retirar mancha da parede).
            </p>
          </div>
          {!isLoading && (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
              aria-label="Fechar"
            >
              <FiX className="h-5 w-5" />
            </button>
          )}
        </div>

        {photoPreviewUrl && (
          <div className="mb-4 overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
            <img
              src={photoPreviewUrl}
              alt="Foto selecionada para edição"
              className="mx-auto h-[min(36vh,280px)] w-full object-contain"
            />
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label
              htmlFor="photo-ai-prompt"
              className="mb-1.5 block text-sm font-medium text-slate-700"
            >
              Instruções de edição
            </label>
            <textarea
              id="photo-ai-prompt"
              value={prompt}
              onChange={(e) => onPromptChange(e.target.value)}
              disabled={isLoading}
              maxLength={MAX_PHOTO_AI_PROMPT_LENGTH + 50}
              rows={4}
              placeholder="Ex.: remover folhas do chão"
              className="w-full resize-y rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-800 shadow-sm outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:bg-slate-50"
            />
            <p className="mt-1 text-right text-xs text-slate-500">
              {sanitizedLength}/{MAX_PHOTO_AI_PROMPT_LENGTH}
            </p>
          </div>

          {isLoading && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
              <FiLoader className="h-4 w-4 shrink-0 animate-spin" />
              Processando imagem com IA… (até 30 segundos)
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {isLoading ? (
              <button
                type="button"
                onClick={onCancelProcessing}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Cancelar processamento
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Fechar
                </button>
                <button
                  type="submit"
                  className="rounded-lg border-0 bg-[#6EA5FF] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#5c94ed]"
                >
                  Editar com IA
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </Modal>
  );
}
